import React, { memo, useMemo } from 'react';
import { Col, Row, Table, TableProps, Typography } from 'antd';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { formatTipDate } from 'Reports/utils';
import {
  TipsSummaryReport,
  TipsSummaryReportParams,
} from 'utils/apiClient/services/reports/types';

interface Props extends TableProps<TipsSummaryReport> {
  currency: string;
  reportData: Array<TipsSummaryReport> | undefined;
  handleTableChange: (t: TipsSummaryReportParams) => void;
  tableParams: TipsSummaryReportParams;
  isDataLoading: boolean;
}

const { Text } = Typography;
type FormatDateProps = {
  date: string;
};

export function formatDate({ date }: FormatDateProps): React.ReactElement {
  return <span>{formatTipDate(date)}</span>;
}

const MemoizedFormatDate = memo(formatDate);

type CurrencyAmountProps = {
  currency: string;
  amount: number | null;
};

const CurrencyAmount = ({ currency, amount }: CurrencyAmountProps) =>
  amount != null ? (
    <FormattedNumber
      style="currency" // eslint-disable-line react/style-prop-object
      currency={currency}
      value={amount}
    />
  ) : (
    <span>-</span>
  );

const TipsSummaryReportTable = ({
  currency,
  reportData,
  handleTableChange,
  tableParams,
  isDataLoading,
  ...props
}: Props) => {
  const summaryData = useMemo(() => {
    let tax_free_tip_total = 0;
    let taxed_tip_total = 0;
    let tax_amount_total = 0;

    if (reportData !== undefined) {
      reportData.forEach(({ tax_free_tip, taxed_tip, tax_amount }) => {
        tax_free_tip_total += Number(tax_free_tip!);
        taxed_tip_total += Number(taxed_tip);
        tax_amount_total += Number(tax_amount);
      });
    }

    return {
      tax_free_tip_total,
      taxed_tip_total,
      tax_amount_total,
    };
  }, [reportData]);

  return (
    <Row>
      <Col span={24}>
        <Table<TipsSummaryReport>
          css={`
            border-width: 1px 1px 1px 1px;
            border-style: solid;
            border-radius: 1px;
            border-color: rgba(0, 0, 0, 0.06);
            margin-top: 10px;
            overflow-x: hidden;
            overflow-y: auto;
            .ant-table-body {
              overflow-x: hidden !important;
              overflow-y: auto !important;
            }
          `}
          dataSource={reportData}
          rowKey={({ key }) => key!}
          size="middle"
          loading={isDataLoading}
          {...props}
          onChange={(pagination) => handleTableChange({ pagination })}
          scroll={{ x: 0, y: 700 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row css={{ fontWeight: 'bold' }}>
                <Table.Summary.Cell
                  index={0}
                  css={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06);',
                    borderBottom: 'none',
                  }}
                >
                  <FormattedMessage defaultMessage="Total" />
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={1}
                  css={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06);',
                    borderBottom: 'none',
                  }}
                >
                  <Text>
                    <FormattedNumber
                      style="currency" // eslint-disable-line react/style-prop-object
                      currency={currency}
                      value={summaryData.tax_free_tip_total}
                    />
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={2}
                  css={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06);',
                    borderBottom: 'none',
                  }}
                >
                  <Text>
                    <FormattedNumber
                      style="currency" // eslint-disable-line react/style-prop-object
                      currency={currency}
                      value={summaryData.taxed_tip_total}
                    />
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={3}
                  css={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06);',
                    borderBottom: 'none',
                  }}
                >
                  <Text>
                    <FormattedNumber
                      style="currency" // eslint-disable-line react/style-prop-object
                      currency={currency}
                      value={summaryData.tax_amount_total}
                    />
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
          pagination={false}
        >
          <Table.Column<TipsSummaryReport>
            key="date"
            width="25%"
            fixed
            title={<FormattedMessage defaultMessage="Date" />}
            render={(record: TipsSummaryReport) =>
              tableParams?.filters?.type === 'daily' ? (
                <MemoizedFormatDate date={record.start_date} />
              ) : (
                <div>
                  <MemoizedFormatDate date={record.start_date} /> {' - '}
                  <MemoizedFormatDate date={record.end_date} />
                </div>
              )
            }
          />

          <Table.Column<TipsSummaryReport>
            key="tax_free_tip"
            width="20%"
            fixed
            title={<FormattedMessage defaultMessage="Tip tax free" />}
            dataIndex="tax_free_tip"
            render={(amount: number | null) => (
              <CurrencyAmount currency={currency} amount={amount} />
            )}
          />

          <Table.Column<TipsSummaryReport>
            key="taxed_tip"
            width="15%"
            fixed
            title={<FormattedMessage defaultMessage="Tip taxable" />}
            dataIndex="taxed_tip"
            render={(amount: number | null) => (
              <CurrencyAmount currency={currency} amount={amount} />
            )}
          />

          <Table.Column<TipsSummaryReport>
            key="tax_amount"
            width="15%"
            fixed
            title={<FormattedMessage defaultMessage="Tax amount" />}
            dataIndex="tax_amount"
            render={(amount: number | null) => (
              <CurrencyAmount currency={currency} amount={amount} />
            )}
          />
        </Table>
      </Col>
    </Row>
  );
};

export default memo(TipsSummaryReportTable);
